import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { P, usePermissions } from '@/features/permissions';
import { type PatientOut, useDeletePatient } from '../api/patients-api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

/**
 * Deletes a patient chart. Lives in the chart's Danger zone, not on the
 * roster: a stray click there removed charts by mistake. The confirm button
 * stays disabled until the MRN is typed, so no sequence of clicks alone can
 * delete a patient.
 */
export function DeletePatientButton({ patient, onDeleted }: { patient: PatientOut; onDeleted?: () => void }) {
  const { can } = usePermissions();
  const remove = useDeletePatient();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');
  if (!can(P.DELETE_PATIENT)) return null;
  const mrn = patient.medical_record_number;
  const name = [patient.metadata_?.first_name, patient.metadata_?.last_name].filter(Boolean).join(' ') || mrn;
  const matches = typed.trim() === mrn;
  async function confirm() {
    if (remove.isPending || !matches) return;
    setError('');
    try {
      await remove.mutateAsync(patient.id);
      setOpen(false);
      toast.success('Patient deleted');
      onDeleted?.();
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: { detail?: unknown } } }).response;
      setError(
        response?.status === 409 && typeof response.data?.detail === 'string'
          ? response.data.detail
          : response?.status === 403
            ? 'You do not have permission to delete this patient.'
            : 'Could not delete this patient. Refresh the page and try again.',
      );
    }
  }
  return (
    <AlertDialog
      open={open}
      onOpenChange={(value) => {
        if (!remove.isPending) setOpen(value);
      }}>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setError('');
          setTyped('');
          setOpen(true);
        }}>
        <Trash2 />
        Delete patient…
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete {name} (MRN {mrn})?
          </AlertDialogTitle>
          <AlertDialogDescription>
            The chart is removed from Patients and can no longer be opened. Stored documents, encounters and audit
            history are kept for compliance, and the MRN stays reserved. Patients with signed reports, flowsheet
            records or lab results cannot be deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="confirm-delete-mrn">
            Type the MRN <span className="font-mono font-semibold">{mrn}</span> to confirm
          </Label>
          <Input
            id="confirm-delete-mrn"
            autoComplete="off"
            value={typed}
            disabled={remove.isPending}
            onChange={(event) => setTyped(event.target.value)}
          />
          <p className="text-sm font-medium text-destructive">This can't be undone in MohairNotes.</p>
        </div>
        {error && <p role="alert">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={remove.isPending || !matches} onClick={() => void confirm()}>
            {remove.isPending ? 'Deleting…' : 'Delete patient'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
